import { useEffect, useRef, useState } from 'react';
import { SYSTEMS, SYSTEM_LIST } from '../emulator/systems';
import { SHADERS } from '../emulator/video/shaders';
import { DIRECTIONAL_STYLES } from './padLayout';
import type { SystemId } from '../emulator/types';
import { coreStatus, invalidateAvailability } from '../emulator/registry';
import { addBios, deleteBios, formatBytes, listBios } from '../storage/romRepository';
import { estimateStorage, requestPersistence, type StorageEstimate } from '../storage/db';
import { purgeAutosaves, deleteSram, nextPsxMemcardId, MAX_PSX_MEMCARDS, psxMemcardSaveKey, saveSram, createBlankPsxMemcard } from '../storage/saveRepository';
import { requestSync } from '../storage/cloudSync';
import { KeymapSettings } from './KeymapSettings';
import { PsxMemcardPanel } from './PsxMemcardPanel';
import { useStore } from '../state/store';

type CoreStatus = Awaited<ReturnType<typeof coreStatus>>;

/** BIOS を受け付ける機種。必須の PS1 を先頭にする。 */
const BIOS_SYSTEMS: SystemId[] = SYSTEM_LIST.filter((s) => s.acceptsBios)
  .sort((a, b) => Number(b.requiresBios) - Number(a.requiresBios))
  .map((s) => s.id);

export function SettingsScreen() {
  const settings = useStore((s) => s.settings);
  const updateSettings = useStore((s) => s.updateSettings);
  const setView = useStore((s) => s.setView);
  const toast = useStore((s) => s.toast);

  const [cores, setCores] = useState<CoreStatus>([]);
  const [bios, setBios] = useState<Awaited<ReturnType<typeof listBios>>>([]);
  const [storage, setStorage] = useState<StorageEstimate | null>(null);
  const [biosSystem, setBiosSystem] = useState<SystemId>(BIOS_SYSTEMS[0]!);
  const biosInput = useRef<HTMLInputElement | null>(null);

  const reload = () => {
    void coreStatus().then(setCores);
    void listBios().then(setBios);
    void estimateStorage().then(setStorage);
  };

  useEffect(() => {
    reload();
  }, []);

  return (
    <div className="flex h-full flex-col">
      <header className="pt-safe-header shrink-0 border-b border-base-700 bg-[#07090f] px-4 pb-3">
        <div className="flex items-center gap-3">
          <button type="button" className="btn-ghost !min-h-0 !px-3 !py-1.5 !text-xs" onClick={() => setView('library')}>
            ← Retro
          </button>
          <h1 className="text-base font-semibold">設定</h1>
        </div>
      </header>

      <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-4 pb-[calc(5.5rem+env(safe-area-inset-bottom,0px))] pt-4 md:pb-16 landscape:pb-16">
        {/* --------------------------- 表示・操作 --------------------------- */}
        <Section title="操作">
          <Row label="バーチャルパッド">
            <select
              value={settings.showVirtualPad}
              onChange={(e) => void updateSettings({ showVirtualPad: e.target.value as typeof settings.showVirtualPad })}
              className="rounded-lg border border-base-600 bg-base-900 px-2 py-1.5 text-xs"
            >
              <option value="auto">自動（タッチ端末のみ）</option>
              <option value="always">常に表示</option>
              <option value="never">表示しない</option>
            </select>
          </Row>

          <Row label="方向入力">
            <select
              value={settings.padDirectional}
              onChange={(e) =>
                void updateSettings({
                  padDirectional: e.target.value as typeof settings.padDirectional,
                })
              }
              className="rounded-lg border border-base-600 bg-base-900 px-2 py-1.5 text-xs"
              aria-label="方向入力"
            >
              {DIRECTIONAL_STYLES.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </Row>
          <p className="-mt-1 text-[0.7rem] leading-relaxed text-slate-500">
            {DIRECTIONAL_STYLES.find((d) => d.id === settings.padDirectional)?.description}
          </p>

          <Slider
            label={`パッドの濃さ (${Math.round(settings.padOpacity * 100)}%)`}
            value={settings.padOpacity}
            min={0.15}
            max={1}
            step={0.05}
            onChange={(v) => void updateSettings({ padOpacity: v })}
          />
          <Slider
            label={`パッドの大きさ (${settings.padScale.toFixed(2)}x)`}
            value={settings.padScale}
            min={0.7}
            max={1.5}
            step={0.05}
            onChange={(v) => void updateSettings({ padScale: v })}
          />

          <Toggle
            label="方向キーの相反入力を無効化"
            hint="左右・上下の同時押しを無視します。実機の挙動に近くなります。"
            checked={settings.blockOpposingDirections}
            onChange={(v) => void updateSettings({ blockOpposingDirections: v })}
          />

          <Toggle
            label="巻き戻し"
            hint="画面右上の巻き戻しボタン（PC は R キー）を押している間、時間を巻き戻します。メモリを消費するため既定では無効です。セーブステートが大きい機種では自動的に無効化されます。"
            checked={settings.rewindEnabled}
            onChange={(v) => void updateSettings({ rewindEnabled: v })}
          />

          <p className="rounded-lg border border-base-600 bg-base-900/60 px-3 py-2 text-[0.65rem] leading-relaxed text-slate-500">
            早送りは押しっぱなしで最大速度（4x）になります。縦持ちの仮想パッドでは速度 − / ＋ / 早送りがパッド帯に出ます。横持ちやキーボードの Tab、コントローラーの R3 にも対応しています。離すと直前の速度に戻ります。
          </p>
        </Section>

        <Section title="キーボード" hint="PC プレイ時のキー割り当て。Esc（メニュー）と Tab（早送り）は変更できません。">
          <KeymapSettings
            keymap={settings.keymap}
            onChange={(keymap) => void updateSettings({ keymap })}
            onMessage={(message, tone) => toast(message, tone ?? 'info')}
          />
        </Section>

        {/* --------------------------- 表示 --------------------------- */}
        <Section title="表示">
          <Toggle
            label="整数倍スケーリング"
            hint="画面の縦を整数倍に丸めます。画面いっぱいには広がらなくなりますが、スクロール時のドットのちらつきが収まります。"
            checked={settings.integerScale}
            onChange={(v) => void updateSettings({ integerScale: v })}
          />

          <Toggle
            label="Worker でエミュレーションする（実験的）"
            hint="エミュレーションを別スレッドへ移し、画面の更新に引きずられにくくします。重いコアほど効きますが、音と入力がスレッドをまたぐぶん遅延が 1 フレーム程度増えます。表示フィルタは使えません。対応していない環境では自動的に通常経路へ戻ります。"
            checked={settings.useWorker}
            onChange={(v) => void updateSettings({ useWorker: v })}
          />

          <div>
            <span className="field-label">表示フィルタ</span>
            <div className="space-y-1.5">
              {SHADERS.map((sh) => (
                <label
                  key={sh.id}
                  className={`flex cursor-pointer items-start gap-2 rounded-lg border p-2 transition-colors ${
                    settings.shader === sh.id ? 'border-accent/50 bg-accent/10' : 'border-base-600 bg-base-900'
                  }`}
                >
                  <input
                    type="radio"
                    name="shader"
                    checked={settings.shader === sh.id}
                    onChange={() => void updateSettings({ shader: sh.id })}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-accent"
                  />
                  <span className="min-w-0">
                    <span className="block text-xs text-slate-200">{sh.name}</span>
                    <span className="block text-[0.65rem] leading-relaxed text-slate-500">{sh.description}</span>
                  </span>
                </label>
              ))}
            </div>
            <p className="mt-1.5 text-[0.65rem] leading-relaxed text-slate-600">
              フィルタを使うと WebGL で描画します。次にゲームを起動したときから反映されます。
              WebGL が使えない環境では自動的に通常描画へ戻ります。
            </p>
          </div>
        </Section>

        {/* --------------------------- 音声 --------------------------- */}
        <Section title="音声">
          <Slider
            label={`音量 (${Math.round(settings.volume * 100)}%)`}
            value={settings.volume}
            min={0}
            max={1}
            step={0.05}
            onChange={(v) => void updateSettings({ volume: v })}
          />
          <Toggle label="ミュート" checked={settings.muted} onChange={(v) => void updateSettings({ muted: v })} />
          <Toggle label="FPS を表示" checked={settings.showFps} onChange={(v) => void updateSettings({ showFps: v })} />
        </Section>

        {/* --------------------------- コア --------------------------- */}
        <Section
          title="エミュレータコア"
          hint="サーバでは public/cores/ に WASM を置いてから npm run build します。未配置だと画面が出ません。詳細は docs/CORES.md。"
        >
          {cores.some((c) => c.backends.every((b) => !b.available || b.id === 'test-pattern')) && (
            <p className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-100">
              実コアが未配置の機種があります。診断用コアしか無いと、まともなゲーム画面にはなりません。
            </p>
          )}
          {cores.map(({ system, backends }) => (
            <div key={system} className="rounded-xl border border-base-600 bg-base-900 p-3">
              <p className="text-xs font-semibold">{SYSTEMS[system].name}</p>
              <ul className="mt-2 space-y-1">
                {backends.map((b) => (
                  <li key={b.id} className="flex items-center justify-between gap-2 text-[0.7rem]">
                    <span className={b.available ? 'text-slate-300' : 'text-slate-600'}>{b.displayName}</span>
                    <span className={b.available ? 'text-accent' : 'text-slate-600'}>
                      {b.available ? '利用可' : '未配置'}
                    </span>
                  </li>
                ))}
              </ul>
              <select
                value={settings.preferredBackends[system] ?? ''}
                onChange={(e) =>
                  void updateSettings({
                    preferredBackends: { ...settings.preferredBackends, [system]: e.target.value || undefined },
                  })
                }
                className="mt-2 w-full rounded-lg border border-base-600 bg-base-800 px-2 py-1.5 text-xs"
              >
                <option value="">自動選択</option>
                {backends
                  .filter((b) => b.available)
                  .map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.displayName}
                    </option>
                  ))}
              </select>
            </div>
          ))}
          <button
            type="button"
            className="btn-ghost w-full"
            onClick={() => {
              invalidateAvailability();
              reload();
              toast('コアの検出をやり直しました');
            }}
          >
            コアを再検出
          </button>
        </Section>

        {/* --------------------------- BIOS --------------------------- */}
        <Section
          title="BIOS"
          hint="PS1 の起動には 512 KiB の BIOS が必須です（例: scph5501.bin）。サイズが違うと HLE に落ち、メモリーカードの容量判定が壊れることがあります。GBA は無くても動きますが、実機 BIOS を入れると起動時の挙動がより正確になります。"
        >
          <input
            ref={biosInput}
            type="file"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (!file) return;
              try {
                await addBios(file, biosSystem);
                toast(`${file.name} を ${SYSTEMS[biosSystem].short} の BIOS として登録しました`, 'success');
                reload();
              } catch (err) {
                toast(err instanceof Error ? err.message : String(err), 'error');
              }
            }}
          />
          {bios.length === 0 ? (
            <p className="text-xs text-slate-500">登録されていません</p>
          ) : (
            <ul className="space-y-1">
              {bios.map((b) => (
                <li key={b.id} className="flex items-center justify-between rounded-lg bg-base-900 px-3 py-2 text-xs">
                  <span className="truncate">
                    <span className="mr-2 rounded bg-base-700 px-1.5 py-0.5 text-[0.6rem] text-slate-300">
                      {SYSTEMS[b.system].short}
                    </span>
                    {b.filename}
                    <span className="ml-2 text-slate-600">{formatBytes(b.size)}</span>
                  </span>
                  <button
                    type="button"
                    className="text-red-400"
                    onClick={async () => {
                      await deleteBios(b.id);
                      reload();
                    }}
                  >
                    削除
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex gap-2">
            <select
              value={biosSystem}
              onChange={(e) => setBiosSystem(e.target.value as SystemId)}
              className="shrink-0 rounded-lg border border-base-600 bg-base-900 px-2 py-1.5 text-xs"
              aria-label="BIOS の機種"
            >
              {BIOS_SYSTEMS.map((id) => (
                <option key={id} value={id}>
                  {SYSTEMS[id].short}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn-ghost min-w-0 flex-1"
              onClick={() => biosInput.current?.click()}
            >
              BIOS を追加
            </button>
          </div>
        </Section>

        {/* --------------------------- PS1 メモリーカード --------------------------- */}
        <Section
          title="PS1 メモリーカード"
          hint="実機と同じく複数枚を切り替えられます。起動中のソフトには反映されず、次に起動したときから使われます。「中身」からセーブの一覧・削除ができます。空き 0 なら容量不足、プレイ画面で「未接続」ならコアへ挿さっていません。"
        >
          <PsxMemcardPanel
            cards={settings.psxMemcards}
            activeId={settings.activePsxMemcardId}
            onSelect={(id) => void updateSettings({ activePsxMemcardId: id })}
            onRename={(id, name) =>
              void updateSettings({
                psxMemcards: settings.psxMemcards.map((c) => (c.id === id ? { ...c, name } : c)),
              })
            }
            onDeleteCard={(id) => {
              const next = settings.psxMemcards.filter((c) => c.id !== id);
              const activeId =
                settings.activePsxMemcardId === id ? (next[0]?.id ?? '0') : settings.activePsxMemcardId;
              void (async () => {
                await deleteSram(psxMemcardSaveKey(id));
                await updateSettings({ psxMemcards: next, activePsxMemcardId: activeId });
                requestSync();
                toast('メモリーカードを削除しました', 'success');
              })();
            }}
            onAddCard={() => {
              const id = nextPsxMemcardId(settings.psxMemcards);
              if (!id) {
                toast(`カードは最大 ${MAX_PSX_MEMCARDS} 枚までです`, 'error');
                return;
              }
              const name = `カード ${settings.psxMemcards.length + 1}`;
              void (async () => {
                await saveSram(psxMemcardSaveKey(id), createBlankPsxMemcard());
                await updateSettings({
                  psxMemcards: [...settings.psxMemcards, { id, name }],
                  activePsxMemcardId: id,
                });
                toast(`「${name}」を追加して選択しました。ソフトを起動し直してください。`, 'success');
              })();
            }}
            canAdd={settings.psxMemcards.length < MAX_PSX_MEMCARDS}
            addDisabledReason={`カード上限（${MAX_PSX_MEMCARDS} 枚）`}
            toast={toast}
          />
        </Section>

        {/* --------------------------- ストレージ --------------------------- */}
        <Section title="ストレージ">
          {storage && (
            <p className="text-xs text-slate-400">
              使用量 {formatBytes(storage.usage)}
              {storage.quota > 0 && ` / ${formatBytes(storage.quota)}`}
              <br />
              永続化: {storage.persisted ? '有効' : '無効'}
            </p>
          )}
          {!storage?.persisted && (
            <button
              type="button"
              className="btn-ghost w-full"
              onClick={async () => {
                const ok = await requestPersistence();
                toast(ok ? 'ストレージを永続化しました' : 'ブラウザに拒否されました', ok ? 'success' : 'error');
                reload();
              }}
            >
              ストレージの永続化を要求
            </button>
          )}
          <button
            type="button"
            className="btn-ghost w-full"
            onClick={async () => {
              const n = await purgeAutosaves();
              toast(`オートセーブを ${n} 件削除しました`);
              reload();
            }}
          >
            オートセーブを削除して容量を空ける
          </button>
        </Section>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2.5">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">{title}</h2>
      {hint && <p className="text-[0.7rem] leading-relaxed text-slate-600">{hint}</p>}
      {children}
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs text-slate-300">{label}</span>
      {children}
    </div>
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start justify-between gap-3">
      <span className="min-w-0">
        <span className="block text-xs text-slate-300">{label}</span>
        {hint && <span className="mt-0.5 block text-[0.65rem] leading-relaxed text-slate-600">{hint}</span>}
      </span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-5 w-5 shrink-0 accent-accent"
      />
    </label>
  );
}

function Slider({
  label,
  value,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
}) {
  return (
    <div>
      <span className="field-label">{label}</span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-accent"
      />
    </div>
  );
}
